import React, { createContext, useContext, useRef } from 'react';

import { cn } from 'helpers/classnames';
import { useRefScrollDimensions } from 'hooks/useRefScrollDimensions';
import Button from 'components/common/Button';
import CloseSmallIcon from 'components/common/IconsV2/CloseSmallIcon';
import CaretLeftSmallIcon from 'components/common/IconsV2/CaretLeftSmallIcon';
import CaretRightSmallIcon from 'components/common/IconsV2/CaretRightSmallIcon';
import IconButton from 'components/common/IconButton';
import { FILTERS_RE } from 'common/regex';
import type { SelectedFilterListContextValue, SelectedFilterListDirection } from 'components/search/SelectedFilterList/SelectedFilterList.types';

const SelectedFilterListContext = createContext<SelectedFilterListContextValue | null>(null);

const useSelectedFilterListContext = () => {
  const context = useContext(SelectedFilterListContext);
  if (!context) {
    throw new Error('SelectedFilterList compound components must be used within SelectedFilterList.Root');
  }
  return context;
};

type SelectedFilterListRootProps = React.ComponentProps<'div'> & {
  hasAutoScroll?: boolean;
};

const SelectedFilterListRoot = ({ className, hasAutoScroll = false, ...props }: SelectedFilterListRootProps) => {
  const itemsRef = useRef<HTMLUListElement>(null);

  const [scrollDimensions, setScrollDimensionsRef] = useRefScrollDimensions({
    includes: {
      scrollLeft: true,
      scrollWidth: true,
      clientWidth: true
    },
    setRef: itemsRef
  });

  const { scrollWidth = 0, clientWidth = 0 } = scrollDimensions || {};
  const maxScroll = scrollWidth - clientWidth;

  const scrollItems = (direction: SelectedFilterListDirection) => {
    if (itemsRef.current) {
      // Scroll by just 1px - scroll-snap will handle snapping to the next item
      const scrollAmount = direction === 'left' ? -1 : 1;

      itemsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const contextValue: SelectedFilterListContextValue = {
    itemsRef,
    scrollDimensions,
    maxScroll,
    scrollItems,
    setScrollDimensionsRef,
    hasAutoScroll
  };

  return (
    <SelectedFilterListContext.Provider value={contextValue}>
      <div className={cn('flex w-full flex-col gap-2', className)} {...props} />
    </SelectedFilterListContext.Provider>
  );
};

type SelectedFilterListHeadingProps = React.ComponentProps<'h2'>;

const SelectedFilterListHeading = ({ className, children, ...props }: SelectedFilterListHeadingProps) => (
  <h2 className={cn('body-l max-md:hidden', className)} {...props}>
    {children}
  </h2>
);

type SelectedFilterListClearFiltersButtonProps = React.ComponentProps<typeof Button>;

const SelectedFilterListClearButton = ({ className, variant = 'text', size = 'small', ...props }: SelectedFilterListClearFiltersButtonProps) => (
  <Button variant={variant} size={size} className={cn('shrink-0 max-md:hidden', className)} {...props} />
);

type SelectedFilterListScrollButtonProps = Partial<React.ComponentProps<typeof IconButton>> & {
  direction: SelectedFilterListDirection;
};

const SelectedFilterListScrollButton = ({
  className,
  direction,
  disabled,
  label = `Scroll ${direction}`,
  size = 'small',
  variant = 'filled',
  ...props
}: SelectedFilterListScrollButtonProps) => {
  const { scrollDimensions, maxScroll, scrollItems, hasAutoScroll } = useSelectedFilterListContext();
  const { scrollLeft = 0, clientWidth = 0, scrollWidth = 0 } = scrollDimensions || {};

  // Only show if hasAutoScroll is enabled and content is overflowing
  const shouldShow = hasAutoScroll && scrollWidth > clientWidth;

  if (!shouldShow) {
    return null;
  }

  return (
    <IconButton
      className={cn('rounded-sm lumon:disabled:bg-gray-100 lumon:disabled:text-gray-400 max-md:hidden', className)}
      disabled={disabled ?? (direction === 'left' ? scrollLeft <= 0 : scrollLeft >= maxScroll - 1)}
      onClick={e => {
        e.preventDefault();
        scrollItems(direction);
      }}
      label={label}
      size={size}
      variant={variant}
      {...props}
    >
      {direction === 'left' ? <CaretLeftSmallIcon size={16} /> : <CaretRightSmallIcon size={16} />}
    </IconButton>
  );
};

type SelectedFilterListFilterItemsProps = React.ComponentProps<'ul'>;

const SelectedFilterListFilterItems = ({ className, ...props }: SelectedFilterListFilterItemsProps) => {
  const { setScrollDimensionsRef } = useSelectedFilterListContext();

  return (
    <ul
      ref={setScrollDimensionsRef}
      className={cn(
        'scrollbar-hidden flex snap-x snap-mandatory items-center gap-4 overflow-x-auto overscroll-none max-md:scroll-px-4 max-md:px-4',
        className
      )}
      {...props}
    />
  );
};

type SelectedFilterListFilterItemProps = React.ComponentProps<'a'> &
  React.ComponentProps<'button'> & {
    removeUrl: string;
  };

const SelectedFilterListFilterItem = ({ className, children, removeUrl, ...props }: SelectedFilterListFilterItemProps) => {
  const baseClassName = 'flex w-full items-center gap-1 rounded-sm bg-gray-100 px-2 py-1 hover:bg-gray-200';

  /*
   * We render different HTML elements based on URL type for SEO reasons:
   *
   * FILTER URLs (/filters/*):
   * - Render as <button> elements (no href attribute)
   * - Prevents Google from discovering/crawling temporary filter combinations
   * - These represent ephemeral UI states, not permanent pages
   *
   * REGULAR URLs (category pages, product pages, etc.):
   * - Render as <a> elements with proper href attributes
   * - Allows normal link behavior (right-click, bookmarking, SEO indexing)
   * - These represent permanent, indexable content
   *
   * See: https://github01.zappos.net/mweb/marty/issues/4869
   */

  const isFilterUrl = FILTERS_RE.test(removeUrl);

  return (
    <li className="shrink-0 snap-start">
      {isFilterUrl ? (
        <button type="button" className={cn(baseClassName, className)} {...props}>
          {children}
          <CloseSmallIcon size={16} />
        </button>
      ) : (
        <a href={removeUrl} className={cn(baseClassName, className)} {...props}>
          {children}
          <CloseSmallIcon size={16} />
        </a>
      )}
    </li>
  );
};

const Root = SelectedFilterListRoot;
const Heading = SelectedFilterListHeading;
const ClearButton = SelectedFilterListClearButton;
const ScrollButton = SelectedFilterListScrollButton;
const FilterItems = SelectedFilterListFilterItems;
const FilterItem = SelectedFilterListFilterItem;

export const SelectedFilterList = { Root, Heading, ClearButton, ScrollButton, FilterItems, FilterItem };
